import React, { useMemo, useState } from "react";
import { theme } from "../theme";
import { Badge } from "./ui";
import { getVisibleWorkspaceNavigation } from "../config/mainWorkspaceNavigation";
import useNotifications from "../hooks/useNotifications";
import {
  NotificationBell,
  NotificationPanel
} from "./notifications";
import GlobalSearch from "./search/GlobalSearch";

export default function SidebarLayout({
  children,
  active = "Dashboard",
  currentPlan = "starter",
  userRole = "preparer",
  onNavigate
}) {
  const navigation = useMemo(
    () => getVisibleWorkspaceNavigation(userRole),
    [userRole]
  );

  const normalizedPlan = String(currentPlan || "starter")
    .trim()
    .toLowerCase();
  const [collapsedSections, setCollapsedSections] =
    useState({});

  const [
    notificationPanelOpen,
    setNotificationPanelOpen
  ] = useState(false);

  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    remove,
    clearAll
  } = useNotifications();

  const handleNavigate = (target) => {
    if (typeof onNavigate !== "function") {
      return;
    }

    if (typeof target === "string") {
      onNavigate(target);
      return;
    }

    if (target?.label) {
      onNavigate(target.label);
    }
  };

  const toggleSection = (section) => {
    setCollapsedSections((current) => ({
      ...current,
      [section]: !current[section]
    }));
  };

  const activeSection = navigation.find((group) =>
    group.items.some((item) => item.label === active)
  );

  const handleSearchNavigate = (targetPage) => {
    handleNavigate(targetPage);
  };

  const handleOpenNotification = (notification) => {
    markRead(notification.id);
    setNotificationPanelOpen(false);

    if (notification.targetPage) {
      handleNavigate(notification.targetPage);
    }
  };

  return (
    <div style={styles.shell}>
      <aside style={styles.sidebar}>
        <button
          type="button"
          onClick={() => handleNavigate("Dashboard")}
          style={styles.brandButton}
          aria-label="Open Ecovanta dashboard"
        >
          <span style={styles.brandLogoFrame} aria-hidden="true">
            <img
              src="/logo/ecovanta-logo.png"
              alt=""
              style={styles.brandLogo}
            />
          </span>

          <div>
            <div style={styles.brandName}>
              Ecovanta
            </div>

            <div style={styles.brandSubtitle}>
              Enterprise workspace
            </div>
          </div>
        </button>

        <div style={styles.badges}>
          <Badge tone="success">
            {String(
              currentPlan || "starter"
            ).toUpperCase()}
          </Badge>

          <Badge tone="default">
            {String(userRole || "preparer")}
          </Badge>
        </div>

        <nav
          aria-label="Main navigation"
          style={styles.navigation}
        >
          {navigation.map((group) => {
            const isCollapsed = Boolean(
              collapsedSections[group.section]
            );

            return (
              <div
                key={group.section}
                style={styles.group}
              >
                <button
                  type="button"
                  onClick={() =>
                    toggleSection(group.section)
                  }
                  style={styles.groupButton}
                  aria-expanded={!isCollapsed}
                >
                  <span>{group.section}</span>

                  <span style={styles.chevron}>
                    {isCollapsed ? "›" : "⌄"}
                  </span>
                </button>

                {!isCollapsed && (
                  <div style={styles.groupItems}>
                    {group.items.map((item) => {
                      const isActive = active === item.label;
                      const requiredPlans = Array.isArray(item.requiredPlans)
                        ? item.requiredPlans.map((plan) =>
                            String(plan).trim().toLowerCase()
                          )
                        : [];
                      const isLocked =
                        requiredPlans.length > 0 &&
                        !requiredPlans.includes(normalizedPlan);

                      return (
                        <button
                          key={`${group.section}-${item.label}`}
                          type="button"
                          onClick={() =>
                            handleNavigate(
                              isLocked ? "Subscription" : item
                            )
                          }
                          aria-current={isActive ? "page" : undefined}
                          aria-label={
                            isLocked
                              ? `${item.label}. Available on Pro and Enterprise plans.`
                              : item.label
                          }
                          title={
                            isLocked
                              ? `${item.label} is available on Pro and Enterprise plans`
                              : item.label
                          }
                          style={{
                            ...styles.navItem,
                            ...(isActive && !isLocked
                              ? styles.activeNavItem
                              : {}),
                            ...(isLocked ? styles.lockedNavItem : {})
                          }}
                        >
                          <span style={styles.icon}>
                            {item.icon || "•"}
                          </span>

                          <span style={styles.navLabel}>
                            {item.label}
                          </span>

                          {isLocked && (
                            <span style={styles.upgradeBadge}>
                              {item.upgradeBadge || "PRO"}
                            </span>
                          )}

                          {!isLocked && item.comingSoon && (
                            <span style={styles.comingSoonBadge}>
                              Soon
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      <main style={styles.main}>
        <div style={styles.workspaceHeader}>
          <div style={styles.breadcrumbs}>
            <button
              type="button"
              onClick={() =>
                handleNavigate("Dashboard")
              }
              style={styles.breadcrumbButton}
            >
              Workspace
            </button>

            <span
              style={styles.breadcrumbSeparator}
            >
              /
            </span>

            {activeSection &&
              activeSection.section !==
                "Workspace" && (
                <>
                  <span>
                    {activeSection.section}
                  </span>

                  <span
                    style={
                      styles.breadcrumbSeparator
                    }
                  >
                    /
                  </span>
                </>
              )}

            <strong
              style={styles.breadcrumbActive}
            >
              {active}
            </strong>
          </div>

          <div style={styles.headerActions}>
            <GlobalSearch onNavigate={handleSearchNavigate} />
            <NotificationBell
              unreadCount={unreadCount}
              onClick={() => setNotificationPanelOpen(true)}
            />
          </div>
        </div>

        {children}
      </main>

      <NotificationPanel
        open={notificationPanelOpen}
        notifications={notifications}
        unreadCount={unreadCount}
        onClose={() =>
          setNotificationPanelOpen(false)
        }
        onOpenNotification={
          handleOpenNotification
        }
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onRemove={remove}
        onClearAll={clearAll}
      />
    </div>
  );
}

const styles = {
  shell: {
    display: "grid",
    gridTemplateColumns:
      "280px minmax(0, 1fr)",
    gap: "24px",
    alignItems: "start"
  },

  sidebar: {
    position: "sticky",
    top: "24px",
    maxHeight: "calc(100vh - 48px)",
    overflowY: "auto",
    background: theme.colors.card,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadow.soft,
    padding: "18px"
  },

  brandButton: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: 0,
    margin: 0,
    border: "none",
    background: "transparent",
    textAlign: "left",
    cursor: "pointer"
  },

  brandLogoFrame: {
    flexShrink: 0,
    width: "44px",
    height: "44px",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    borderRadius: "12px",
    background: "#ffffff"
  },

  brandLogo: {
    width: "68px",
    height: "68px",
    maxWidth: "none",
    objectFit: "contain",
    display: "block"
  },

  brandName: {
    fontSize: "20px",
    fontWeight: 900,
    color: theme.colors.text,
    letterSpacing: "-0.02em"
  },

  brandSubtitle: {
    color: theme.colors.muted,
    fontSize: "12px",
    marginTop: "2px"
  },

  badges: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    margin: "18px 0"
  },

  navigation: {
    display: "grid",
    gap: "14px"
  },

  group: {
    display: "grid",
    gap: "6px"
  },

  groupButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "4px 6px",
    border: "none",
    background: "transparent",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    cursor: "pointer"
  },

  chevron: {
    fontSize: "16px",
    lineHeight: 1
  },

  groupItems: {
    display: "grid",
    gap: "5px"
  },

  navItem: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    textAlign: "left",
    padding: "10px 11px",
    borderRadius: "10px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontWeight: 650,
    background: "transparent",
    color: theme.colors.text,
    transition:
      "background 0.18s ease, border-color 0.18s ease, transform 0.18s ease"
  },

  activeNavItem: {
    background: "#ecfdf5",
    borderColor: "#a7f3d0",
    color: theme.colors.primary,
    boxShadow: "inset 3px 0 0 #0f766e"
  },

  lockedNavItem: {
    color: "#64748b",
    background: "#f8fafc",
    borderColor: "#e2e8f0"
  },

  icon: {
    flexShrink: 0,
    width: "20px",
    display: "inline-flex",
    justifyContent: "center",
    fontSize: "15px"
  },

  navLabel: {
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  upgradeBadge: {
    flexShrink: 0,
    padding: "2px 7px",
    borderRadius: "999px",
    background: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fed7aa",
    fontSize: "9px",
    fontWeight: 900,
    letterSpacing: "0.04em"
  },

  comingSoonBadge: {
    flexShrink: 0,
    padding: "2px 6px",
    borderRadius: "999px",
    background: "#f1f5f9",
    color: "#64748b",
    fontSize: "9px",
    fontWeight: 800,
    textTransform: "uppercase"
  },

  main: {
    minWidth: 0
  },

  workspaceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px"
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    minWidth: 0
  },

  breadcrumbs: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    color: "#64748b",
    fontSize: "13px",
    marginBottom: 0
  },

  breadcrumbButton: {
    border: "none",
    padding: 0,
    background: "transparent",
    color: "#0f766e",
    fontWeight: 700,
    cursor: "pointer"
  },

  breadcrumbSeparator: {
    color: "#cbd5e1"
  },

  breadcrumbActive: {
    color: "#0f172a"
  }
};